import { useState, useEffect, useCallback } from 'react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/visitor-beacon`;

const VISITOR_ID_KEY = 'cybertronic_visitor_id';
const SESSION_ID_KEY = 'cybertronic_session_id';
const SESSION_LAST_KEY = 'cybertronic_session_last';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

function uuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getVisitorId(): string {
  let id = localStorage.getItem(VISITOR_ID_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(VISITOR_ID_KEY, id);
  }
  return id;
}

function getSessionId(): string {
  const lastStr = localStorage.getItem(SESSION_LAST_KEY);
  const last = lastStr ? parseInt(lastStr, 10) : 0;
  const now = Date.now();

  // New session if no prior session, or last activity was > 30 min ago
  if (!last || now - last > SESSION_TIMEOUT_MS) {
    const id = uuid();
    localStorage.setItem(SESSION_ID_KEY, id);
    localStorage.setItem(SESSION_LAST_KEY, String(now));
    return id;
  }

  let id = localStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(SESSION_ID_KEY, id);
  }
  localStorage.setItem(SESSION_LAST_KEY, String(now));
  return id;
}

function touchSession() {
  localStorage.setItem(SESSION_LAST_KEY, String(Date.now()));
}

let beaconInFlight = false;
async function sendPing(path: string) {
  if (beaconInFlight) return;
  if (!SUPABASE_URL || !ANON_KEY) return;
  beaconInFlight = true;
  try {
    await fetch(`${FUNCTION_URL}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        path,
        referrer: typeof document !== 'undefined' ? document.referrer || null : null,
      }),
      keepalive: true,
    });
  } catch {
    // Silent failure — analytics should never break the UI
  } finally {
    beaconInFlight = false;
  }
}

export interface AnalyticsStats {
  activeNow: number;
  totalVisitors: number;
  totalPageViews: number;
  visitors24h: number;
  pageViews24h: number;
  visitorsToday: number;
  visitorsYesterday: number;
  topPages: { path: string; count: number }[];
  hourlyVisitors: number[];
  sessionTimeoutMinutes: number;
  activeThresholdSeconds: number;
}

function getAdminSecret(): string {
  return localStorage.getItem('admin_token') || '';
}

function adminHeaders(): Record<string, string> {
  return {
    Authorization: `Bearer ${ANON_KEY}`,
    'X-Admin-Token': getAdminSecret(),
  };
}

/**
 * Tracks page views from the browser. Only sends beacons when the user has
 * accepted analytics cookies (or when no cookie banner applies).
 */
export function trackPageView(path: string) {
  const consent = localStorage.getItem('cybertronic_cookie_consent');
  if (consent === 'declined') return;
  touchSession();
  sendPing(path);
}

/**
 * Hook that loads admin analytics stats from the visitor-beacon edge function.
 */
export function useSiteAnalytics() {
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!getAdminSecret()) {
        throw new Error('Pro zobrazení statistik se přihlaste jako administrátor.');
      }
      const res = await fetch(`${FUNCTION_URL}/stats`, { headers: adminHeaders() });
      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_logged_in');
        throw new Error('Platnost přihlášení vypršela. Přihlaste se prosím znovu jako administrátor.');
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed (${res.status})`);
      }
      const data = (await res.json()) as AnalyticsStats;
      setStats(data);
    } catch (err: any) {
      setError(err.message ?? 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    // Refresh every 60 seconds so "active now" stays current
    const interval = setInterval(fetchStats, 60_000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  return { stats, loading, error, refresh: fetchStats };
}

/**
 * Hook to be mounted once at the app root — sends a page-view ping on every
 * route change. Respects cookie consent.
 */
export function useVisitorTracking() {
  useEffect(() => {
    // Track initial load
    trackPageView(window.location.pathname + window.location.search);

    const handleRouteChange = () => {
      trackPageView(window.location.pathname + window.location.search);
    };

    // React Router doesn't always fire popstate, so we also listen to it
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);
}
